/**
 * Integration Models
 * 
 * @fileoverview Defines TypeScript interfaces for managing various integration settings,
 * mappings, and events within the site settings integrations module. Includes support
 * for multiple integration providers such as iCIMS, Payworks, and Payzaar.
 * 
 * @module integrations.model
 * 
 * @interface IntegrationSettings - Core integration configuration with failure email handling
 * @interface IntegrationType - Integration provider identification
 * @interface IcimsIntegrationSettings - iCIMS-specific integration details
 * @interface IntegrationMapping - Field mapping between internal tables and external systems
 * @interface PayworksIntegrationDetails - Payworks authentication and configuration
 * @interface PayworksIntegrationDetailsSubmit - Payworks submission schema with culture as string
 * @interface PayworksIntegrationMapping - Payworks field mapping with transformation rules
 * @interface PayworksIntegrationMappingSubmit - Payworks mapping submission schema
 * @interface IntegrationEvent - Integration event record with processing status
 * @interface IntegrationEventVerbose - Extended integration event with detailed logs and payload
 * @interface PayworksTestConnectionResponse - Payworks connection test result
 * @interface PayworksMappingHubbubTable - Hubbub table reference with parent relationship
 * @interface PayzaarIntegrationDetails - Payzaar cloud storage configuration
 * @interface PayzaarIntegrationDetailsSubmit - Payzaar submission schema
 * @interface PayzaarTestConnectionResponse - Payzaar connection test result
 * 
 * @see Version from @app/shared/models/version.model
 */
import { Version } from "@app/shared/models/version.model"

export interface IntegrationSettings {
    id: string,
    description: any,
    integration: IntegrationType,
    status: boolean,
    failureEmail: {
        subject: string,
        body: string,
        recipients: string[]
    }
    version: Version
}


export interface IntegrationType {
    id: string,
    name: string
}

export interface IcimsIntegrationSettings {
    defaultOrganization: {
        id: string,
        name: string,
        parent: string
    },
    customerId: number,
    clientId: string,
    clientSecret: string,
    createdOn: string,
    createdBy: {
        id: string,
        firstName: string,
        lastName: string
    },
    culture: {
        id: string,
        text: string
    },
    authenticationEndpoint: string
}

export interface IntegrationMapping {
    id: string,
    integration: IntegrationType,
    table: {
        id: string,
        name: string,
    },
    field: {
        id: string,
        name: string,
        format: string
    },
    status: boolean,
    mandatory: boolean,
    externalField: string,
}

export interface PayworksIntegrationDetails {
    clientId: string,
    clientSecret: string,
    customerNumber: string,
    username: string,
    password: string,
    culture: {
        id: string,
        text: string
    }
}

export interface PayworksIntegrationDetailsSubmit {
    clientId: string,
    clientSecret: string,
    customerNumber: string,
    username: string,
    password: string,
    culture: string
}

export interface PayworksIntegrationMapping {
    id: string,
    table: {
        id: string,
        name: string,
    },
    tableField: {
        id: string,
        name: string,
        format: string
    },
    transformations: {
      hubbub: string,
      integrationTarget: string
    }[],
    integrationFieldId: string,
    version: Version
}

export interface PayworksIntegrationMappingSubmit {
    integrationSetting: string,
    tableField: string,
    integrationFieldId: string,
    transformations: {
        hubbub: string,
        payworks: string
    }[],
}

export interface IntegrationEvent {
    id: string,
    timestamp: string,
    processed: boolean,
    integration: {
        id: string,
        name: string
    },
    affectedUser: {
        id: string,
        firstName: string
        lastName: string
    },
    results: string,
    status: {
        id: string,
        name: string
    },
    version: Version
}

export interface IntegrationEventVerbose {
    id: string,
    timestamp: string,
    processed: boolean,
    processedTimestamp: string,
    payload: string,
    integration: {
        id: string,
        name: string
    },
    affectedUser: {
        id: string,
        firstName: string
        lastName: string
    },
    logs: {
        createdOn: string,
        message: string
    }[]
    results: string,
    status: {
        id: string,
        name: string
    },
    version: Version
}

export interface PayworksTestConnectionResponse {
    success: boolean,
    message: string
}

export interface PayworksMappingHubbubTable {
    id: string,
    name: string
    parentTable: {
        id: string,
        name: string
    }
}

/*
 
   ____                                   ___       _                       _   _             
  |  _ \ __ _ _   _ ______ _  __ _ _ __  |_ _|_ __ | |_ ___  __ _ _ __ __ _| |_(_) ___  _ __  
  | |_) / _` | | | |_  / _` |/ _` | '__|  | || '_ \| __/ _ \/ _` | '__/ _` | __| |/ _ \| '_ \ 
  |  __/ (_| | |_| |/ / (_| | (_| | |     | || | | | ||  __/ (_| | | | (_| | |_| | (_) | | | |
  |_|   \__,_|\__, /___\__,_|\__,_|_|    |___|_| |_|\__\___|\__, |_|  \__,_|\__|_|\___/|_| |_|
              |___/                                         |___/                             
 
*/

export interface PayzaarIntegrationDetails {
    id: string,
    createdOn: string,
    createdBy: {
        id: string,
        firstName: string,
        lastName: string
    },
    azureStorage: {
        storageAccountName: string,
        containerName: string,
        sasToken: string
    },
    schedule: {
        dayOfMonth: number,
        hour: number,
        minute: number,
        timezone: {
            id: string,
            utcStandardOffset: string,
            standardName: string,
            displayName: string
        }
    },
    culture: {
        id: string,
        text: string
    },
    employeeFilter: string
}

export interface PayzaarIntegrationDetailsSubmit {
    azureStorage: {
        storageAccountName: string,
        containerName: string,
        sasToken: string
    },
    schedule: {
        dayOfMonth: number,
        hour: number,
        minute: number,
        timezoneId: string
    },
    cultureId: string,
    employeeFilter: string
}

export interface PayzaarTestConnectionResponse {
    success: boolean,
    message: string
}